import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import {
  ChoicePromptCard,
  type ChoicePromptCardProps,
} from 'features/chat/components/choice-prompt-card';
import { hasAddressForms } from 'features/conversation-preferences/address-form';
import {
  chooseOnboardingAddressForm,
  chooseOnboardingIndustry,
  chooseOnboardingSalutation,
  reopenOnboardingSalutation,
} from '../onboarding-chat';
import { ONBOARDING_INDUSTRY_OPTIONS, ONBOARDING_INDUSTRY_OTHER } from '../onboarding-industry';
import {
  ONBOARDING_AREA_STEP,
  ONBOARDING_INDUSTRY_STEP,
  ONBOARDING_SALUTATION_STEP,
  ONBOARDING_TOOLS_STEP,
  type OnboardingAnswers,
} from '../onboarding-journey';
import {
  formatOnboardingList,
  ONBOARDING_ADDRESS_OPTIONS,
  ONBOARDING_TEAM_OPTIONS,
  ONBOARDING_TOOL_OPTIONS,
} from '../onboarding-script';
import { ONBOARDING_SALUTATION_OPTIONS } from '../onboarding-salutation';

type SetStep = (next: number, answers?: OnboardingAnswers) => void;

/** Props every step card shares: the skip chip of the catch-up, the question as group name. */
export type CommonCardProps = Pick<
  ChoicePromptCardProps,
  'otherLabel' | 'disabled' | 'ariaLabel' | 'skipLabel' | 'onSkip'
>;

type StepCardProps = {
  step: number;
  isDone: boolean;
  disabled?: boolean;
  conversationId: string | undefined;
  answers: OnboardingAnswers;
  language: string | undefined;
  commonCardProps: CommonCardProps;
  answer: (text: string) => void;
  setStep: SetStep;
};

type ConversationCardProps = Omit<StepCardProps, 'conversationId' | 'step' | 'isDone'> & {
  conversationId: string;
};

/** Step 1: in the catch-up the du/Sie chips (while the form is unknown), else the salutation. */
function SalutationStepCard({
  conversationId,
  answers,
  language,
  commonCardProps,
  answer,
  setStep,
}: Readonly<ConversationCardProps>) {
  const { t } = useTranslation();
  const asksAddressForm = answers.catchUp && !answers.addressForm && hasAddressForms(language);

  if (asksAddressForm) {
    const addressOptions = ONBOARDING_ADDRESS_OPTIONS.map(({ value, key, defaultValue }) => ({
      value,
      label: t(key, { defaultValue }),
    }));
    return (
      <ChoicePromptCard
        {...commonCardProps}
        otherLabel={undefined}
        options={addressOptions}
        onSelect={(option) =>
          chooseOnboardingAddressForm(
            conversationId,
            option.value === 'informal' ? 'informal' : 'formal',
            setStep
          )
        }
      />
    );
  }

  const salutationOptions = ONBOARDING_SALUTATION_OPTIONS.map(({ value, key, defaultValue }) => ({
    value,
    label: t(key, { defaultValue, familyName: answers.familyName }),
  }));
  return (
    <ChoicePromptCard
      {...commonCardProps}
      otherLabel={t('ONBOARDING_SALUTATION_OTHER', { defaultValue: 'Something else …' })}
      options={salutationOptions}
      submitLabel={t('SUBMIT', { defaultValue: 'Submit' })}
      onSelect={(option) =>
        chooseOnboardingSalutation(
          conversationId,
          option.value as 'frau' | 'herr' | 'none',
          setStep
        )
      }
      onOtherSubmit={answer}
    />
  );
}

/** Step 2: the 8 industry chips plus free text, and [Anrede ändern] under a guessed salutation. */
function IndustryStepCard({
  conversationId,
  answers,
  commonCardProps,
  answer,
  setStep,
}: Readonly<ConversationCardProps>) {
  const { t } = useTranslation();
  const industryOptions = ONBOARDING_INDUSTRY_OPTIONS.map(({ code, key, defaultValue }) => ({
    value: code,
    label: t(key, { defaultValue }),
  }));
  return (
    <>
      {answers.salutationInferred && !answers.catchUp ? (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => reopenOnboardingSalutation(conversationId, setStep)}
          >
            {t('ONBOARDING_SALUTATION_FIX', { defaultValue: 'Change salutation' })}
          </Button>
        </div>
      ) : null}
      <ChoicePromptCard
        {...commonCardProps}
        otherLabel={t(ONBOARDING_INDUSTRY_OTHER.key, {
          defaultValue: ONBOARDING_INDUSTRY_OTHER.defaultValue,
        })}
        options={industryOptions}
        submitLabel={t('SUBMIT', { defaultValue: 'Submit' })}
        onSelect={(option) =>
          chooseOnboardingIndustry(
            conversationId,
            option.value as (typeof ONBOARDING_INDUSTRY_OPTIONS)[number]['code'],
            setStep
          )
        }
        onOtherSubmit={answer}
      />
    </>
  );
}

/** Step 3: the area chips. */
function AreaStepCard({ commonCardProps, answer }: Readonly<Omit<StepCardProps, 'step'>>) {
  const { t } = useTranslation();
  const teamOptions = ONBOARDING_TEAM_OPTIONS.map(({ key, defaultValue }) => {
    const label = t(key, { defaultValue });
    return { value: label, label };
  });
  return (
    <ChoicePromptCard
      {...commonCardProps}
      options={teamOptions}
      submitLabel={t('SUBMIT', { defaultValue: 'Submit' })}
      onSelect={(option) => answer(option.label)}
      onOtherSubmit={answer}
    />
  );
}

/** Step 4: the tools, several at once, answered as one list in the UI language. */
function ToolsStepCard({
  commonCardProps,
  answer,
  language,
}: Readonly<Omit<StepCardProps, 'step'>>) {
  const { t } = useTranslation();
  const toolOptions = ONBOARDING_TOOL_OPTIONS.map(({ key, defaultValue }) => {
    const label = t(key, { defaultValue });
    const icon =
      key === 'ONBOARDING_TOOL_GOOGLE_DOCS' ? (
        <FileText className="size-4" strokeWidth={1.5} />
      ) : undefined;
    return icon ? { value: label, label, icon } : { value: label, label };
  });
  return (
    <ChoicePromptCard
      {...commonCardProps}
      options={toolOptions}
      multiple
      submitLabel={t('SUBMIT', { defaultValue: 'Submit' })}
      onSubmit={(options, otherText) =>
        answer(
          formatOnboardingList([...options.map((option) => option.label), otherText], language)
        )
      }
      onOtherSubmit={answer}
    />
  );
}

/** The chips of the current step. Nothing while disabled; the industry and tools steps show
 *  nothing once the journey is done (the completion card takes over). */
export function OnboardingStepCard(props: Readonly<StepCardProps>) {
  const { step, isDone, disabled, conversationId } = props;
  if (disabled) return null;
  if (step === ONBOARDING_AREA_STEP) return <AreaStepCard {...props} />;
  if (step === ONBOARDING_TOOLS_STEP) return isDone ? null : <ToolsStepCard {...props} />;
  if (!conversationId) return null;
  if (step === ONBOARDING_SALUTATION_STEP) {
    return <SalutationStepCard {...props} conversationId={conversationId} />;
  }
  if (step === ONBOARDING_INDUSTRY_STEP && !isDone) {
    return <IndustryStepCard {...props} conversationId={conversationId} />;
  }
  return null;
}
